/**
 * Leads and marketing, as this app calls the API: prospects and their timelines, email campaigns,
 * the do-not-email list and the marketing settings. Kept apart from `crm.ts` because it is one
 * self-contained surface; a refusal throws an `ApiError` carrying the server's sentence.
 */
import type {
  AddLeadActivityRequest,
  AddSuppressionRequest,
  CampaignDetailDto,
  CampaignDto,
  CampaignPreviewDto,
  ConvertLeadRequest,
  ConvertLeadResult,
  CreateLeadRequest,
  LeadDetailDto,
  LeadDto,
  LeadListDto,
  LeadSource,
  LeadStatus,
  MarketingSettingsDto,
  SaveCampaignRequest,
  SetLeadStatusRequest,
  SuppressionDto,
  UpdateLeadRequest,
  UpdateMarketingSettingsRequest
} from '../contracts.gen.js';
import { request } from './http.js';

export interface LeadFilter {
  status?: LeadStatus;
  source?: LeadSource;
  ownerUserId?: number;
  tag?: string;
  search?: string;
  followUpDue?: boolean;
  includeClosed?: boolean;
}

export function getLeads(filter: LeadFilter, signal?: AbortSignal): Promise<LeadListDto> {
  const q = new URLSearchParams();
  if (filter.status !== undefined) q.set('status', String(filter.status));
  if (filter.source !== undefined) q.set('source', String(filter.source));
  if (filter.ownerUserId !== undefined) q.set('ownerUserId', String(filter.ownerUserId));
  if (filter.tag?.trim()) q.set('tag', filter.tag.trim());
  if (filter.search?.trim()) q.set('search', filter.search.trim());
  if (filter.followUpDue) q.set('followUpDue', 'true');
  if (filter.includeClosed) q.set('includeClosed', 'true');
  const query = q.toString();
  return request<LeadListDto>('/api/leads' + (query ? '?' + query : ''), { signal });
}

export function getLeadTags(signal?: AbortSignal): Promise<string[]> {
  return request<string[]>('/api/leads/tags', { signal }).then(r => r ?? []);
}

export function getLead(id: number, signal?: AbortSignal): Promise<LeadDetailDto> {
  return request<LeadDetailDto>(`/api/leads/${id}`, { signal });
}

export function createLead(req: CreateLeadRequest): Promise<LeadDto> {
  return request<LeadDto>('/api/leads', { method: 'POST', body: req });
}

export function updateLead(id: number, req: UpdateLeadRequest): Promise<LeadDto> {
  return request<LeadDto>(`/api/leads/${id}`, { method: 'PUT', body: req });
}

export function setLeadStatus(id: number, req: SetLeadStatusRequest): Promise<LeadDto> {
  return request<LeadDto>(`/api/leads/${id}/status`, { method: 'POST', body: req });
}

export function addLeadActivity(id: number, req: AddLeadActivityRequest): Promise<LeadDetailDto> {
  return request<LeadDetailDto>(`/api/leads/${id}/activities`, { method: 'POST', body: req });
}

export function deleteLeadActivity(id: number, activityId: number): Promise<LeadDetailDto> {
  return request<LeadDetailDto>(`/api/leads/${id}/activities/${activityId}`, { method: 'DELETE' });
}

export function convertLead(id: number, req: ConvertLeadRequest): Promise<ConvertLeadResult> {
  return request<ConvertLeadResult>(`/api/leads/${id}/convert`, { method: 'POST', body: req });
}

export function deleteLead(id: number): Promise<void> {
  return request<void>(`/api/leads/${id}`, { method: 'DELETE' });
}

// ---- campaigns ----

export function getCampaigns(signal?: AbortSignal): Promise<CampaignDto[]> {
  return request<CampaignDto[]>('/api/marketing/campaigns', { signal }).then(r => r ?? []);
}

export function getCampaign(id: number, signal?: AbortSignal): Promise<CampaignDetailDto> {
  return request<CampaignDetailDto>(`/api/marketing/campaigns/${id}`, { signal });
}

export function createCampaign(req: SaveCampaignRequest): Promise<CampaignDto> {
  return request<CampaignDto>('/api/marketing/campaigns', { method: 'POST', body: req });
}

export function updateCampaign(id: number, req: SaveCampaignRequest): Promise<CampaignDto> {
  return request<CampaignDto>(`/api/marketing/campaigns/${id}`, { method: 'PUT', body: req });
}

export function duplicateCampaign(id: number): Promise<CampaignDto> {
  return request<CampaignDto>(`/api/marketing/campaigns/${id}/duplicate`, { method: 'POST' });
}

export function deleteCampaign(id: number): Promise<void> {
  return request<void>(`/api/marketing/campaigns/${id}`, { method: 'DELETE' });
}

export function previewCampaign(req: SaveCampaignRequest, signal?: AbortSignal): Promise<CampaignPreviewDto> {
  return request<CampaignPreviewDto>('/api/marketing/preview', { method: 'POST', body: req, signal });
}

export function sendCampaign(id: number): Promise<CampaignDto> {
  return request<CampaignDto>(`/api/marketing/campaigns/${id}/send`, { method: 'POST' });
}

export function cancelCampaign(id: number): Promise<CampaignDto> {
  return request<CampaignDto>(`/api/marketing/campaigns/${id}/cancel`, { method: 'POST' });
}

// ---- the do-not-email list ----

export function getSuppressions(search?: string, signal?: AbortSignal): Promise<SuppressionDto[]> {
  const q = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
  return request<SuppressionDto[]>('/api/marketing/suppressions' + q, { signal }).then(r => r ?? []);
}

export function addSuppression(req: AddSuppressionRequest): Promise<SuppressionDto> {
  return request<SuppressionDto>('/api/marketing/suppressions', { method: 'POST', body: req });
}

export function removeSuppression(id: number): Promise<void> {
  return request<void>(`/api/marketing/suppressions/${id}`, { method: 'DELETE' });
}

// ---- settings ----

export function getMarketingSettings(signal?: AbortSignal): Promise<MarketingSettingsDto> {
  return request<MarketingSettingsDto>('/api/marketing/settings', { signal });
}

export function updateMarketingSettings(req: UpdateMarketingSettingsRequest): Promise<MarketingSettingsDto> {
  return request<MarketingSettingsDto>('/api/marketing/settings', { method: 'PUT', body: req });
}
